import React, { useMemo, useRef } from "react";
import MiniRuleTree from "./RuleTree/RuleChat/MiniRuleTree";
import RuleArtifactCard from "./RuleTree/RuleChat/RuleArtifactCard";
import "./RuleTree/ruleAgentTheme.css";

/* The rule engine's half of the SDK's renderer contract. Two identifiers travel between the user
   and the agent, in opposite directions, and they are deliberately not the same one:

       ```qi-rule-artifact        the agent proposes a rule. Draggable, insertable.
       {"title": "...", "rule": {"tree_data": [...]}}

       ```qi-rule-selection       the person points at a branch of their own rule. Read-only.
       {"label": "...", "tree_data": [...]}

   One is an offer, the other is a reference, and they carry different payloads — sniffing one
   identifier for both shapes would only hide that. Parsing, judging and drawing either of them
   belongs here; the SDK matches the identifier and hands the body over verbatim. */

export const RULE_ARTIFACT_IDENTIFIER = "qi-rule-artifact";
export const RULE_SELECTION_IDENTIFIER = "qi-rule-selection";

/* What goes out when the person attaches a branch and types nothing. The SDK has no sentence to
   offer about a rule, so this one is ours — it is the same wording the old chat used. */
export const SELECTION_PROMPT = "O que estes trechos fazem nesta regra?";

function noInsert() {}

function RuleArtifactBlock({ content, host }) {
    /* Throwing is the documented way to decline: the SDK answers it with the monospace block, so a
       payload the model got wrong stays readable instead of leaving a hole in the conversation. */
    let artifact = JSON.parse(content);
    if (!artifact || typeof artifact !== "object" || !artifact.rule) {
        throw new Error("qi-rule-artifact carries no rule object");
    }

    let { rulesContext, userVariablesAvailable, onInsertNodes } = host.current;
    return (
        <RuleArtifactCard
            artifact={artifact}
            rulesContext={rulesContext}
            userVariablesAvailable={userVariablesAvailable}
            onInsertNodes={typeof onInsertNodes === "function" ? onInsertNodes : noInsert}
        />
    );
}

/* Drawn in two places by the same function and looking the same in both: the chip in the composer
   before sending, and the sub-bubble inside the message afterwards. It is a reference to something
   already on screen, so it does not drag and has no insert button — that is the artifact card's
   job, and the difference between the two is the point of having two identifiers.

   The label is in the payload but not drawn here: the composer chip already prints it above this
   body, and inside the bubble the branch's own nodes say what it is. */
function RuleSelectionBlock({ content }) {
    let selection = JSON.parse(content);
    if (!selection || !Array.isArray(selection.tree_data)) {
        throw new Error("qi-rule-selection carries no tree_data");
    }

    return <MiniRuleTree treeData={selection.tree_data} />;
}

/* The array is built once and never again. The SDK carries it down a context, so a fresh array on
   every render would redraw every fenced block of the conversation on every keystroke of a stream.
   A ref is what lets the identity stay put while the values the card needs stay current — the
   engines rebuild `onInsertNodes` and `userVariablesAvailable` on each of their own renders. */
export function useRuleArtifactRenderers({ rulesContext, userVariablesAvailable, onInsertNodes }) {
    const host = useRef({});
    host.current = { rulesContext, userVariablesAvailable, onInsertNodes };

    return useMemo(
        () => [
            {
                identifier: RULE_ARTIFACT_IDENTIFIER,
                renderer: ({ content }) => <RuleArtifactBlock content={content} host={host} />
            },
            {
                identifier: RULE_SELECTION_IDENTIFIER,
                renderer: ({ content }) => <RuleSelectionBlock content={content} />
            }
        ],
        []
    );
}

/* The engine holds the selection; this turns one selected branch into what the SDK stages. The id
   is the branch's own root id, which is what lets the engine map a removal straight back to its
   selection without keeping a second list in sync. */
export function toSelectionAttachment({ rootId, label, treeData }) {
    return {
        id: rootId,
        identifier: RULE_SELECTION_IDENTIFIER,
        content: JSON.stringify({ label: label, tree_data: treeData }),
        label: label,
        defaultPrompt: SELECTION_PROMPT
    };
}
